import { contentFrames } from '../../content-media/content-media';
import type {
  ClaimsInput,
  ProductModelView,
  ReviewCaseView,
} from '../adapters/product-review-port';
import '../ui/product-review.css';
import { durableProductFacts } from './durable-state';

/** Decode the stored 06 canonical UTF-8 byte-length frames without normalizing any field. */
export function exactSubmittedClaims(review: ReviewCaseView): ClaimsInput | null {
  const { immutableContent, sourceEvidence } = review.submitted;
  if (!immutableContent || !sourceEvidence) return null;
  const bytes = new TextEncoder().encode(immutableContent);
  const values: string[] = [];
  let offset = 0;
  try {
    const decoder = new TextDecoder('utf-8', { fatal: true });
    for (let index = 0; index < contentFrames(immutableContent).length; index += 1) {
      const colon = bytes.indexOf(58, offset);
      if (colon < 0) return null;
      const lengthText = decoder.decode(bytes.slice(offset, colon));
      if (!/^(0|[1-9][0-9]*)$/.test(lengthText)) return null;
      const length = Number(lengthText);
      if (!Number.isSafeInteger(length) || length < 1 || length > bytes.length - colon - 1)
        return null;
      offset = colon + 1;
      values.push(decoder.decode(bytes.slice(offset, offset + length)));
      offset += length;
    }
    if (
      offset !== bytes.length ||
      !(values.length === 4 || (values.length === 6 && values[4] === 'CA04-CONTENT-V1'))
    )
      return null;
    return {
      modelName: values[0]!,
      category: values[1]!,
      manufacturerClaims: values[2]!,
      source: values[3]!,
    };
  } catch {
    return null;
  }
}

export function currentSubmissionReviews(
  product: ProductModelView,
  reviews: readonly ReviewCaseView[],
) {
  const latest = durableProductFacts(product).submission;
  if (!latest) return [];
  return reviews.filter(
    ({ submitted }) =>
      submitted.productId === product.productId &&
      submitted.country === product.country &&
      submitted.supplier === product.supplier &&
      submitted.revision === latest.revision &&
      submitted.digest === latest.digest,
  );
}

export function SubmittedReviewContent({ review }: { readonly review: ReviewCaseView }) {
  const content = exactSubmittedClaims(review);
  return (
    <section className="cm-section" aria-label="不可变审核内容">
      <h3>不可变提交内容</h3>
      {content ? (
        <dl className="cm-facts">
          {(['modelName', 'category', 'manufacturerClaims', 'source'] as const).map(
            (key, index) => (
              <div key={key}>
                <dt>{['产品名称型号', '产品类别', '厂家声明', '资料来源'][index]}</dt>
                <dd style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{content[key]}</dd>
              </div>
            ),
          )}
        </dl>
      ) : (
        <p>不可变内容不可用或格式异常，禁止审核通过。请重新读取，不能使用当前可变草稿代替。</p>
      )}
      <details className="cm-audit">
        <summary>查看原始不可变内容与摘要</summary>
        <p>摘要：{review.submitted.digest}</p>
        <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {review.submitted.immutableContent ?? 'UNAVAILABLE'}
        </pre>
      </details>
    </section>
  );
}
